import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams, Navigate } from "react-router-dom";
import axios from "axios";
import { Search, ArrowRight } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Motion";
import ProductCard from "@/components/ProductCard";
import { API, CATEGORY_LABELS, money, setJsonLd, removeJsonLd, SITE_ORIGIN } from "@/lib/site";
import { MagicFlexContent } from "@/pages/MagicFlex";
import { DimsportCompatibility } from "@/components/DimsportCompatibility";

const POPULAR_FLEX_GROUPS = [
  { id: "flex-regal-slave", label: "Regal Tuning Slaves", slugs: ["flex-slave-obd-bench-regal", "flex-slave-full-regal"] },
  { id: "flex-any-master", label: "Slave to Any Master", slugs: ["flex-slave-obd-bench-any", "flex-slave-full-any"] },
  { id: "flex-master-options", label: "Master", slugs: ["flex-master-obd-bench", "flex-master-full"] },
];
const POPULAR_FLEX = POPULAR_FLEX_GROUPS.flatMap((g) => g.slugs);
// Legacy generic FLEX products — superseded by the six Popular FLEX
// Configurations; hidden from the Magic Motorsport journey (DB records kept).
const LEGACY_FLEX = ["flex-master", "flex-slave", "flex-protocol-package"];

const KESS3_REGAL = [
  "kess3-slave-regal-cars-obd", "kess3-slave-regal-cars-obd-bench", "kess3-slave-regal-full",
];

const CATS = [
  { slug: "alientech", tag: "KESS3 · K-TAG" },
  { slug: "autotuner", tag: "OBD · Bench" },
  { slug: "magic-motorsport", tag: "FLEX" },
  { slug: "dimsport", tag: "Genius · Trasdata" },
  { slug: "used-equipment", tag: "Tested & Warrantied" },
  { slug: "accessories", tag: "Cables · Power · Frames" },
  { slug: "subscriptions", tag: "Updates & Protocols" },
];

export default function Tools() {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [sort, setSort] = useState("");
  const [flexCfg, setFlexCfg] = useState(null);
  const [kess3Cfg, setKess3Cfg] = useState(null);

  useEffect(() => {
    axios.get(`${API}/flex-config`).then((r) => setFlexCfg(r.data)).catch(() => {});
    axios.get(`${API}/kess3-config`).then((r) => setKess3Cfg(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (q) params.set("q", q);
    if (sort) params.set("sort", sort);
    axios.get(`${API}/products?${params}`).then((r) => setProducts(r.data)).catch(() => setProducts([])).finally(() => setLoading(false));
  }, [category, q, sort]);

  useEffect(() => {
    if (!category) { removeJsonLd("ld-breadcrumb"); return; }
    const origin = SITE_ORIGIN;
    setJsonLd("ld-breadcrumb", {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin + "/" },
        { "@type": "ListItem", position: 2, name: "Tuning Tools", item: origin + "/tuning-tools" },
        { "@type": "ListItem", position: 3, name: CATEGORY_LABELS[category] || category, item: origin + `/tuning-tools/${category}` },
      ],
    });
    return () => removeJsonLd("ld-breadcrumb");
  }, [category]);

  // AutoTuner has a dedicated SEO landing page — the category URL redirects there.
  if (category === "autotuner") return <Navigate to="/autotuner" replace />;

  // Magic Motorsport category leads with the full FLEX SEO content (shared
  // component with /magicmotorsport-flex, which remains the canonical URL).
  const showMagicLayout = category === "magic-motorsport" && !q;

  return (
    <main data-testid="tools-page" className="pt-32 sm:pt-40 pb-24">
      {showMagicLayout && <MagicFlexContent embedded />}
      <div className="container-regal">
        {!showMagicLayout && (
        <SectionHead
          as="h1"
          overline="Shop — Tuning Equipment"
          title={category === "alientech" ? "Alientech KESS3 Tools" : category ? CATEGORY_LABELS[category] : "Professional Tuning Tools"}
          copy="Genuine Master and Slave tools, subscriptions, accessories and tested used equipment — supplied with Regal technical support."
        />
        )}

        <div className="mb-10 flex flex-wrap gap-2.5" data-testid="category-filters">
          <Link
            to="/tuning-tools"
            data-testid="filter-all"
            className={`px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.16em] border transition-colors duration-300 ${!category ? "border-regal-amber bg-regal-amber text-void font-bold" : "border-line text-neutral-300 hover:border-regal-amber hover:text-regal-amber"}`}
          >
            All Equipment
          </Link>
          {CATS.map((c) => (
            <Link
              key={c.slug}
              to={c.slug === "autotuner" ? "/autotuner" : c.slug === "alientech" ? "/alientech-kess3" : `/tuning-tools/${c.slug}`}
              data-testid={`filter-${c.slug}`}
              className={`px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.16em] border transition-colors duration-300 ${category === c.slug ? "border-regal-amber bg-regal-amber text-void font-bold" : "border-line text-neutral-300 hover:border-regal-amber hover:text-regal-amber"}`}
            >
              {CATEGORY_LABELS[c.slug]}
            </Link>
          ))}
        </div>

        <div className="mb-10 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <label className="relative block sm:w-80">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search tools…"
              data-testid="product-search-input"
              className="w-full border border-line bg-card py-3 pl-10 pr-4 text-sm text-neutral-100 outline-none placeholder:text-neutral-600 focus:border-regal-amber transition-colors"
            />
          </label>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            data-testid="product-sort-select"
            className="border border-line bg-card px-4 py-3 text-sm text-neutral-200 outline-none focus:border-regal-amber"
          >
            <option value="">Sort: Featured</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
          </select>
        </div>

        {loading ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[3/4] animate-pulse border border-line bg-card" />)}
          </div>
        ) : products.length === 0 ? (
          <p className="border border-line bg-card p-12 text-center text-sm text-neutral-400" data-testid="no-products-message">No products match your search.</p>
        ) : category === "alientech" && !q ? (
          <>
            <Reveal><p className="overline-label mb-6">KESS3 — Choose Your Setup</p></Reveal>
            <div className="grid gap-5 sm:grid-cols-3" data-testid="kess3-setup-grid">
              <Reveal>
                <Link to="/build-your-kess3/master" data-testid="kess3-master-card" className="group flex h-full flex-col border border-line bg-white shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:border-regal-amber/40 hover:shadow-[0_16px_40px_rgba(16,24,40,0.10)] transition-all duration-300">
                  <div className="overflow-hidden border-b border-line">
                    <img src="/assets/brands/alientech.webp" alt="Alientech KESS3 Master" loading="lazy" className="aspect-[16/8] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <p className="font-display text-xl font-bold uppercase tracking-wide text-ink">KESS3 Master</p>
                    <p className="mt-2 text-sm leading-relaxed text-neutral-400">Build your own Master configuration — hardware plus the exact Master activations you need.</p>
                    <div className="mt-auto flex items-center justify-between pt-6">
                      <p className="font-mono text-xs uppercase tracking-[0.14em] text-regal-amber">Hardware {money(kess3Cfg?.hardware_price ?? 500, kess3Cfg?.currency || "GBP")} + activations</p>
                      <span className="inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.16em] text-ink group-hover:text-regal-amber transition-colors">Configure <ArrowRight size={14} /></span>
                    </div>
                  </div>
                </Link>
              </Reveal>
              <Reveal delay={0.07}>
                <a href="#kess3-regal-slave" data-testid="kess3-regal-card" className="group flex h-full flex-col border border-regal-amber/40 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:shadow-[0_16px_40px_rgba(16,24,40,0.10)] transition-all duration-300">
                  <div className="overflow-hidden border-b border-line">
                    <img src="/assets/brands/alientech.webp" alt="Alientech KESS3 Slave linked to Regal Tuning" loading="lazy" className="aspect-[16/8] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-regal-amber mb-2">Best value</p>
                    <p className="font-display text-xl font-bold uppercase tracking-wide text-ink">KESS3 Slave — Regal Tuning</p>
                    <p className="mt-2 text-sm leading-relaxed text-neutral-400">Best-value Slave packages linked to Regal Tuning as your Master. From {money(1100, "GBP")} all-in.</p>
                    <div className="mt-auto flex items-center justify-between pt-6">
                      <p className="font-mono text-xs uppercase tracking-[0.14em] text-regal-amber">3 ready-made packages</p>
                      <span className="inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.16em] text-ink group-hover:text-regal-amber transition-colors">View packages <ArrowRight size={14} /></span>
                    </div>
                  </div>
                </a>
              </Reveal>
              <Reveal delay={0.14}>
                <Link to="/build-your-kess3/slave" data-testid="kess3-slave-card" className="group flex h-full flex-col border border-line bg-white shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:border-regal-amber/40 hover:shadow-[0_16px_40px_rgba(16,24,40,0.10)] transition-all duration-300">
                  <div className="overflow-hidden border-b border-line">
                    <img src="/assets/brands/alientech.webp" alt="Alientech KESS3 Slave for any Master" loading="lazy" className="aspect-[16/8] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <p className="font-display text-xl font-bold uppercase tracking-wide text-ink">KESS3 Slave — Any Master</p>
                    <p className="mt-2 text-sm leading-relaxed text-neutral-400">Build your own Slave configuration for use with your chosen Master.</p>
                    <div className="mt-auto flex items-center justify-between pt-6">
                      <p className="font-mono text-xs uppercase tracking-[0.14em] text-regal-amber">Hardware {money(kess3Cfg?.hardware_price ?? 500, kess3Cfg?.currency || "GBP")} + activations</p>
                      <span className="inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.16em] text-ink group-hover:text-regal-amber transition-colors">Configure <ArrowRight size={14} /></span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            </div>

            <div id="kess3-regal-slave" className="mt-16 scroll-mt-28" data-testid="kess3-regal-slave-section">
              <Reveal><p className="overline-label mb-2">Best value — linked to Regal Tuning</p></Reveal>
              <Reveal delay={0.05}><h3 className="mb-8 font-display text-2xl font-bold uppercase tracking-tight text-ink">KESS3 Slave — Regal Tuning packages</h3></Reveal>
              <div className="grid gap-5 sm:grid-cols-3">
                {KESS3_REGAL.map((slug) => products.find((x) => x.slug === slug)).filter(Boolean).map((p, i) => <Reveal key={p.slug} delay={i * 0.06}><ProductCard p={p} /></Reveal>)}
              </div>
            </div>

            {products.filter((p) => !KESS3_REGAL.includes(p.slug)).length > 0 && (
              <div className="mt-16">
                <Reveal><p className="overline-label mb-6">All Alientech Products</p></Reveal>
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="product-grid">
                  {products.filter((p) => !KESS3_REGAL.includes(p.slug)).map((p, i) => <Reveal key={p.slug} delay={Math.min(i, 5) * 0.05}><ProductCard p={p} /></Reveal>)}
                </div>
              </div>
            )}
          </>
        ) : category === "magic-motorsport" && !q ? (
          <>
            <div data-testid="build-your-flex-section">
              <Reveal><p className="overline-label mb-2">Need a different setup?</p></Reveal>
              <Reveal delay={0.05}><h3 className="mb-8 font-display text-2xl font-bold uppercase tracking-tight text-ink">Build Your FLEX</h3></Reveal>
              <div className="grid gap-5 sm:grid-cols-2">
                {[
                  ["master", "Build Your FLEX – Master", "FLEX hardware plus your choice of Master software packages."],
                  ["slave", "Build Your FLEX – Slave", "FLEX hardware plus Slave software packages — link to Regal Tuning or your own Master."],
                ].map(([t, title, d], i) => (
                  <Reveal key={t} delay={i * 0.07}>
                    <Link to={`/build-your-flex/${t}`} data-testid={`build-flex-${t}-card`} className="group flex h-full flex-col border border-line bg-white shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:border-regal-amber/40 hover:shadow-[0_16px_40px_rgba(16,24,40,0.10)] transition-all duration-300">
                      <div className="overflow-hidden border-b border-line">
                        <img src="/assets/brands/magic-motorsport.webp" alt={title} loading="lazy" className="aspect-[16/8] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                      </div>
                      <div className="flex flex-1 flex-col p-7">
                        <p className="font-display text-xl font-bold uppercase tracking-wide text-ink">{title}</p>
                        <p className="mt-2 text-sm leading-relaxed text-neutral-400">{d}</p>
                        <div className="mt-auto flex items-center justify-between pt-6">
                          <p className="font-mono text-xs uppercase tracking-[0.14em] text-regal-amber">Hardware {money(flexCfg?.hardware_price ?? 800, flexCfg?.currency || "GBP")} + software</p>
                          <span className="inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.16em] text-ink group-hover:text-regal-amber transition-colors">Configure <ArrowRight size={14} /></span>
                        </div>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>

            <div className="mt-16" id="flex-products">
              <Reveal><p className="overline-label mb-6">Popular FLEX Configurations</p></Reveal>
              {POPULAR_FLEX_GROUPS.map((g) => {
                const items = g.slugs.map((slug) => products.find((x) => x.slug === slug)).filter(Boolean);
                if (!items.length) return null;
                return (
                  <div key={g.id} id={g.id} className="mb-10 scroll-mt-32" data-testid={`flex-group-${g.id}`}>
                    <Reveal><p className="mb-4 font-mono text-[11px] uppercase tracking-[0.24em] text-regal-amber">{g.label}</p></Reveal>
                    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid={g.id === "flex-regal-slave" ? "flex-popular-grid" : undefined}>
                      {items.map((p, i) => <Reveal key={p.slug} delay={Math.min(i, 5) * 0.05}><ProductCard p={p} /></Reveal>)}
                    </div>
                  </div>
                );
              })}
            </div>

            {products.filter((p) => !POPULAR_FLEX.includes(p.slug) && !LEGACY_FLEX.includes(p.slug)).length > 0 && (
              <div className="mt-16">
                <Reveal><p className="overline-label mb-6">All Magic Motorsport Products</p></Reveal>
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="product-grid">
                  {products.filter((p) => !POPULAR_FLEX.includes(p.slug) && !LEGACY_FLEX.includes(p.slug)).map((p, i) => <Reveal key={p.slug} delay={Math.min(i, 5) * 0.05}><ProductCard p={p} /></Reveal>)}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className={category === "dimsport" && !q ? "" : "grid gap-5 sm:grid-cols-2 lg:grid-cols-3"} data-testid="product-grid">
            {category === "dimsport" && !q ? (
              <>
                <div className="mb-16" data-testid="dimsport-compat-wrap">
                  <DimsportCompatibility />
                </div>
                <div id="dimsport-products" className="scroll-mt-32">
                  <Reveal><p className="overline-label mb-6">Dimsport Products</p></Reveal>
                  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {products.map((p, i) => <Reveal key={p.slug} delay={Math.min(i, 5) * 0.05}><ProductCard p={p} /></Reveal>)}
                  </div>
                </div>
              </>
            ) : (
              products.map((p, i) => <Reveal key={p.slug} delay={Math.min(i, 5) * 0.05}><ProductCard p={p} /></Reveal>)
            )}
          </div>
        )}
      </div>
    </main>
  );
}
